import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col } from 'react-bootstrap';
import { find, map } from 'lodash';
import clsx from 'clsx';
import { addTrendKpi, removeSelectedKpi } from '../../actions';
import SectionInfoButton from './SectionInfoButton';
import { Checkbox } from 'sharedComponents/Checkbox';
import { IconCondition } from 'svgIcons/MotionPortalIcons';
import { limitIndications, MAX_INPUT_CHAR_LENGTH } from 'helpers/constants';
import useUserCapability from 'sharedHooks/useUserCapability';
import { SearchBox } from 'sharedComponents/SearchBox';
import { MAX_SELECTED_KPIS } from '../constants';

const ParameterSection = (props) => {
    const { t: translate } = useTranslation();
    const userCapabilities = useUserCapability();
    const [searchboxFilter, setSearchboxFilter] = useState('');
    const {
        section,
        selectedTrendsData,
        asset
    } = props;
    const dispatch = useDispatch();

    if (!section || !section.data || section.data.length === 0) {
        return null;
    }
    const toggleMeasurement = (kpi, isSelected) => {
        if (isSelected) {
            dispatch(removeSelectedKpi(kpi));
        }
        else {
            dispatch(addTrendKpi(kpi, asset));
        }
    };
    const { data } = section;
    let kpiData = data;
    const serchBoxValue = searchboxFilter.toLowerCase();
    if (serchBoxValue !== '' && serchBoxValue !== null) {
        kpiData = data?.filter((kpi) => {
            return kpi?.label?.toLowerCase().includes(serchBoxValue);
        });
    }

    const { selectedKpiItems, unselectedKpiItems } = kpiData.reduce((acc, kpi) => {
        if (kpi.timeseriesKey) {
            const isSelected = selectedTrendsData.some(trend => trend.config?.timeseriesKey === kpi.timeseriesKey);
            if (isSelected) {
                acc.selectedKpiItems.push(kpi);
            } else {
                acc.unselectedKpiItems.push(kpi);
            }
        }
        return acc;
    }, { selectedKpiItems: [], unselectedKpiItems: [] });

    // Combine selected and unselected KPI items to maintain the desired order for rendering
    const sortedKpiData = [...selectedKpiItems, ...unselectedKpiItems];

    return <Col xl lg={12} className='measurement-list'>
        <div className='measurement-list-title'>
            <div className='measurment-list-buttons'>
                <div className='measurement-list-title-text'>
                    <div>{translate('ABB.Powertrain.Frontend.detailedInformationOperationalParametersTab')}</div>
                    <SectionInfoButton section={section} />
                </div>
                <div className='signal-group-header-info'>
                    <div className='kpisignal__filter__search'>
                        <SearchBox
                            placeholder={translate('ABB.Powertrain.Frontend.searchSignal')}
                            onChange={(searchTerm) => setSearchboxFilter(searchTerm)}
                            value={searchboxFilter}
                            maxLength={MAX_INPUT_CHAR_LENGTH} />
                    </div>
                    <span>{selectedTrendsData.length}/{MAX_SELECTED_KPIS}{' '}{translate('ABB.Powertrain.Frontend.selected')}</span>
                </div>
            </div>
        </div>
        <div className='measurement-list-head'>
            <div className='measurement-list-item'>
                <div className='data-command-checkbox'></div>
                <div className='data-label'>{translate('ABB.Powertrain.Frontend.operationalParametersNameLabel')}</div>
                <div className='data-value'>{translate('ABB.Powertrain.Frontend.opertaionalParametersLastMeasurementLabel')}</div>
            </div>
        </div>
        <div className='measurement-list-body'>
            {map(sortedKpiData, (kpi) => {
                const isSelected = !!find(selectedTrendsData, (selectedTrendKPI) => kpi.timeseriesKey === selectedTrendKPI?.config?.timeseriesKey);
                return <button
                    type='button'
                    className={clsx('measurement-list-item', isSelected && 'selected-measurement-list-item')}
                    onClick={() => kpi?.isPlottableToTrendChart && toggleMeasurement(kpi, isSelected)}
                    aria-pressed={isSelected}
                    key={kpi.timeseriesKey}>
                    <div className='data-command-checkbox'>
                        <Checkbox checked={isSelected}
                            disabled={!kpi?.isPlottableToTrendChart}
                        />
                    </div>
                    <div className='data-label'>
                        <div className='condition-icon-label'>
                            {
                                userCapabilities?.hasOperationalParametersLimitStatus() &&
                                <div className='condition-icon'>
                                    {kpi?.limitStatus ?
                                        <IconCondition condition={limitIndications[kpi?.limitStatus]?.iconID} /> :
                                        null
                                    }
                                </div>
                            }
                            <div>{kpi.label}</div>
                        </div>
                        {kpi?.unit && <div className='data-label__unit'>{kpi?.unit}</div>}
                    </div>
                    <div className='data-value'>{kpi.displayValue ?? '-'}</div>
                </button>;
            })}
        </div>
    </Col>;
};

ParameterSection.propTypes = {
    section: PropTypes.shape({
        data: PropTypes.arrayOf(
            PropTypes.shape({
                timeseriesKey: PropTypes.string, // Unique key for the timeseries
                label: PropTypes.string, // Label for the KPI
                unit: PropTypes.string, // Unit of the KPI
                displayValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Last measurement value
                isPlottableToTrendChart: PropTypes.bool, // Whether the KPI can be plotted on the trend chart
                limitStatus: PropTypes.string, // Status of the limit
            })
        ), // Array of KPI data
        sectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Section ID
    }), // Section details
    selectedTrendsData: PropTypes.arrayOf(
        PropTypes.shape({
            config: PropTypes.shape({
                timeseriesKey: PropTypes.string, // Timeseries key of the selected trend
            }),
        })
    ), // Array of selected trends data
    asset: PropTypes.shape({
        id: PropTypes.string, // Asset ID
        name: PropTypes.string, // Asset name
    }), // Asset details
};

export default ParameterSection;
